import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { notification } from '@insuma/mpp-ui';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  AVOID_SHOWING_ERRORS_CODES,
  AVOID_SHOWING_ERRORS_ENDPOINTS,
  EHTTPErrorCode,
  UNEXPECTED_ERROR_MESSAGE,
} from 'core/constants/error-http.constants';
import { APPLICATION_ID } from 'core/constants/general.constants';
import { DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS } from 'core/constants/msal.constants';
import { IServiceError } from 'core/model/interfaces/error.interface';
import { isTestingEnv } from 'shared/utils/env';
import { composePromises } from 'shared/utils/function-utils';
import msalPublicClientApplication from 'shared/utils/msal.utils';
import { generateTraceParent } from 'shared/utils/trace-parent.utils';

export const acquireAccessToken = async (msalInstance: PublicClientApplication) => {
  const activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length === 0)
    throw new Error('Unauthenticated user is trying to acquire an access token');

  const account = activeAccount || accounts[0];

  const requestConfig = {
    account,
    scopes: DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS.scopes,
  };

  try {
    const token = await msalInstance.acquireTokenSilent(requestConfig);
    return token.accessToken;
  } catch (error) {
    // eslint-disable-next-line no-console
    if (!isTestingEnv()) console.warn(`Token not found error ${error}`);
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.handleRedirectPromise();
      await msalInstance.acquireTokenRedirect({
        ...DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS,
        account,
      });
    } else {
      // eslint-disable-next-line no-console
      console.error(
        'A non-interaction-related error occurred while acquiring the token silently. This may indicate a configuration issue or a network problem. Error details:',
        error,
      );
      await msalInstance.loginRedirect({
        ...DEFAULT_MSAL_LOGIN_REDIRECT_PARAMETERS,
        account,
      });
    }
  }
};

export const injectToken = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const newConfig = { ...config };
  try {
    const token = await acquireAccessToken(msalPublicClientApplication);
    newConfig.headers.Authorization = `Bearer ${token}`;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Error acquiring a new token', e);
  }

  return newConfig;
};

export const injectApplicationId = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const newConfig = { ...config };
  newConfig.headers['Application-Id'] = APPLICATION_ID;
  return newConfig;
};

export const injectTraceParent = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  const newConfig = { ...config };
  if (!window.newrelic) newConfig.headers.Traceparent = generateTraceParent();
  return newConfig;
};

export const injectHeaders = composePromises<InternalAxiosRequestConfig>(
  injectToken,
  injectApplicationId,
  injectTraceParent,
);

const shouldAvoidNotification = (message: string, response?: AxiosResponse) =>
  response?.status === EHTTPErrorCode.UNAUTHORIZED ||
  AVOID_SHOWING_ERRORS_CODES.includes(response?.data?.code) ||
  message === 'canceled' ||
  AVOID_SHOWING_ERRORS_ENDPOINTS.some(endpointItem => {
    const isEndpointMatched = new RegExp(endpointItem.url).test(response?.config?.url ?? '');
    if (endpointItem.method) {
      return isEndpointMatched && endpointItem.method === response?.config?.method;
    }
    return isEndpointMatched;
  });

export const handleCommonErrors = ({ response, message: messageAxios }: AxiosError<IServiceError>) => {
  if (shouldAvoidNotification(messageAxios, response)) return;
  const message = response?.data?.error?.message || UNEXPECTED_ERROR_MESSAGE;
  notification.error(message, {
    duration: 5000,
  });
};

export const getErrorFromService = (error: unknown, defaultErrorMessage?: string) => {
  let errorMessage = defaultErrorMessage || 'Error inesperado';

  if (axios.isAxiosError<IServiceError>(error)) {
    const serviceError = error.response?.data?.error;
    if (serviceError?.message) errorMessage = serviceError.message;
  }

  return errorMessage;
};
